// Un solo lugar para "la carga de esta sección falló", compartido por todas las
// secciones del escritorio de la mesa.
//
// Motivo (#374): desde que el backend exige rol de agente, cualquier usuario no
// agente recibe 403 en `GET /serviceDesk/metrics|tickets|settings|tickets/:id`.
// Las secciones cargaban con `.then(...).finally(...)` SIN `.catch`, así que el
// 403 quedaba en una promesa sin capturar y la pantalla se quedaba en "Cargando…"
// para siempre (dashboard, SLAs, configuración, ficha) o mostraba "Sin tickets."
// (lista, kanban). El texto de remediación que el gate trae en `meta.message`
// —"pide al admin reseller que agregue tu rol en Configuración → Roles de
// agente"— no se veía en ninguna pantalla.
//
// Aquí se captura el error, se distingue el 403 de autorización de un fallo
// cualquiera y se pinta el mensaje DEL BACKEND (no uno inventado en el front,
// que se desincronizaría con la remediación real).
import { useCallback, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage } from "../services/envelope";

// Red de seguridad: si el 403 llega sin cuerpo (proxy, CORS), se enseña el
// mismo texto que AGENT_ONLY_MESSAGE en serviceDeskAccess.js.
export const AGENT_ONLY_FALLBACK =
  "Solo un agente de la mesa de servicio puede usar el escritorio de la mesa. " +
  "Pide al admin reseller que agregue tu rol en Configuración → Roles de agente.";

const GENERIC_FALLBACK = "No se pudo cargar esta sección de la mesa de servicio.";

export interface DeskLoadError {
  /** true = 403 de autorización (no es agente / marca fuera de su alcance). */
  forbidden: boolean;
  /** Mensaje del backend; nunca vacío. */
  message: string;
}

export function isForbidden(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 403;
}

export function describeDeskLoadError(err: unknown): DeskLoadError {
  const forbidden = isForbidden(err);
  return {
    forbidden,
    message: apiErrorMessage(err, forbidden ? AGENT_ONLY_FALLBACK : GENERIC_FALLBACK),
  };
}

/**
 * Estado + `capture` para pasar directo a `.catch(capture)` en la carga de
 * datos de una sección. `reset()` antes de recargar (cambio de filtro).
 */
export function useDeskLoadError() {
  const [error, setError] = useState<DeskLoadError | null>(null);
  const capture = useCallback((err: unknown) => {
    setError(describeDeskLoadError(err));
  }, []);
  const reset = useCallback(() => setError(null), []);
  return { error, capture, reset };
}

/** Panel en página. Devuelve `null` cuando no hay error, para usarlo en línea. */
export function DeskLoadErrorPanel({ error }: { error: DeskLoadError | null }) {
  const { isDarkMode } = useTheme();
  if (!error) return null;

  const title = error.forbidden
    ? "No tienes acceso al escritorio de la mesa"
    : "No se pudo cargar la información";
  const tone = error.forbidden
    ? isDarkMode
      ? "border-amber-500/40 bg-amber-500/10"
      : "border-amber-300 bg-amber-50"
    : isDarkMode
      ? "border-red-500/40 bg-red-500/10"
      : "border-red-300 bg-red-50";
  const titleColor = error.forbidden ? "text-amber-600" : "text-red-600";
  const body = isDarkMode ? "text-gray-300" : "text-gray-700";

  return (
    <div className="p-6" role="alert">
      <div className={`mx-auto max-w-2xl rounded-xl border p-6 text-center ${tone}`}>
        <i
          className={`${error.forbidden ? "ri-lock-line" : "ri-error-warning-line"} text-3xl ${titleColor}`}
          aria-hidden="true"
        />
        <h2 className={`mt-2 text-base font-semibold ${titleColor}`}>{title}</h2>
        <p className={`mt-2 text-sm ${body}`}>{error.message}</p>
      </div>
    </div>
  );
}
